/**
 * Reload into a new build after a deploy.
 *
 * iOS Safari keeps tabs (and home-screen web apps) alive for days without a
 * real reload, so a phone can keep running an old bundle long after a release.
 * On start and whenever the tab becomes visible again we fetch
 * /assets/version.json (no-store) and reload once if it names a different
 * version than the running bundle.
 */
const RELOAD_FLAG = 'app-update-reloaded-for';

export function shouldReloadForVersion(running: string, deployed: string, alreadyReloadedFor: string | null): boolean {
  if (!running || !deployed || running === 'dev' || deployed === 'dev') {
    return false;
  }
  if (running === deployed) {
    return false;
  }
  // Reload at most once per deployed version, so a stale CDN/proxy copy of the
  // bundle can never cause a reload loop.
  return alreadyReloadedFor !== deployed;
}

export function startAppUpdateCheck(runningVersion: string, win: Window = window): void {
  if (!runningVersion || runningVersion === 'dev' || typeof fetch !== 'function') {
    return;
  }
  let checking = false;
  const check = () => {
    if (checking) {
      return;
    }
    checking = true;
    fetch('/assets/version.json?ts=' + Date.now(), { cache: 'no-store' })
      .then(r => (r.ok ? r.json() : null))
      .then((body: any) => {
        const deployed = body && typeof body.version === 'string' ? body.version.trim() : '';
        let flag: string | null = null;
        try { flag = win.sessionStorage.getItem(RELOAD_FLAG); } catch (e) { /* private mode */ }
        if (shouldReloadForVersion(runningVersion, deployed, flag)) {
          try { win.sessionStorage.setItem(RELOAD_FLAG, deployed); } catch (e) { /* ignore */ }
          win.location.reload();
        }
      })
      .catch(() => { /* offline: try again next time */ })
      .then(() => { checking = false; });
  };
  check();
  win.document.addEventListener('visibilitychange', () => {
    if (win.document.visibilityState === 'visible') {
      check();
    }
  });
  win.addEventListener('pageshow', (e: any) => {
    if (e && e.persisted) {
      check(); // restored from the back/forward cache
    }
  });
}
